import { PropTypes } from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import CreateTemplateModal from "./components/CreateTemplateModal";
import TemplateSelect from "./components/TemplateSelect";
import { getCurrentUserId, isAdminUser } from "./helpers/currentUser";
import useTemplateManager from "./hooks/useTemplateManager";

const AppointmentTemplateSelect = ({
  applyTemplate = () => false,
  subject = "",
  body = "",
  duration = 60,
  time = "",
  timezone = "",
  location = "",
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const isAdmin = isAdminUser();
  const currentUserId = getCurrentUserId();

  const formState = useMemo(
    () => ({ subject, body, duration, time, timezone, location }),
    [subject, body, duration, time, timezone, location],
  );

  const isDifferent = useCallback((currentFormState, template) => {
    const subjectChanged =
      currentFormState.subject !== (template.subject || "");
    const bodyChanged = currentFormState.body !== (template.body || "");
    const durationChanged =
      (currentFormState.duration ?? null) !== (template.duration ?? null);
    const timeChanged = currentFormState.time !== (template.time || "");
    const timezoneChanged =
      currentFormState.timezone !== (template.timezone || "");
    const locationChanged =
      currentFormState.location !== (template.location || "");
    return (
      subjectChanged ||
      bodyChanged ||
      durationChanged ||
      timeChanged ||
      timezoneChanged ||
      locationChanged
    );
  }, []);

  const {
    templates,
    selectedTemplate,
    setSelectedTemplate,
    templateChanged,
    setTemplateChanged,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isCopying,
    isSharing,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    copyTemplate,
    shareTemplate,
    unshareTemplate,
    applyTemplate: trackApply,
  } = useTemplateManager({
    apiPath: "/appointment_templates",
    apiResourceName: "appointment_template",
    templateType: "appointment",
    displayNameSingular: "Appointment Template",
    displayNamePlural: "Appointment Templates",
    formState,
    isDifferent,
    onTemplateCreated: () => {
      setShowCreateModal(false);
    },
  });

  const handleCreate = (payload) => {
    createTemplate(() => payload);
  };

  const updateAppointmentTemplate = () => {
    if (selectedTemplate?.value) {
      updateTemplate(() => ({
        id: selectedTemplate.value,
        subject,
        body,
        duration,
        time,
        timezone,
        location,
      }));
    }
  };

  return (
    <>
      <TemplateSelect
        templates={templates}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        templateChanged={templateChanged}
        setTemplateChanged={setTemplateChanged}
        isLoading={isLoading}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        isCopying={isCopying}
        isSharing={isSharing}
        updateTemplate={updateAppointmentTemplate}
        deleteTemplate={deleteTemplate}
        copyTemplate={copyTemplate}
        shareTemplate={shareTemplate}
        unshareTemplate={unshareTemplate}
        trackApply={trackApply}
        applyTemplate={applyTemplate}
        onCreate={() => setShowCreateModal(true)}
        isAdmin={isAdmin}
        currentUserId={currentUserId}
        infoTooltip="Templates save your meeting title, details, duration, time, timezone, and location. Date and attendees are set per appointment."
      />
      <CreateTemplateModal
        templateType="appointment"
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onCreate={handleCreate}
        isCreating={isCreating}
        isAdmin={isAdmin}
        formValues={formState}
      />
    </>
  );
};

AppointmentTemplateSelect.propTypes = {
  applyTemplate: PropTypes.func,
  subject: PropTypes.string,
  body: PropTypes.string,
  duration: PropTypes.number,
  time: PropTypes.string,
  timezone: PropTypes.string,
  location: PropTypes.string,
};

export default AppointmentTemplateSelect;
